import React, { useState, useMemo, useEffect, useCallback, useRef } from "react"
import ClassNames from "classnames"
import {
  map,
  range,
  chunk,
  size,
  get,
  filter,
  reject,
  findKey,
  isEmpty,
  includes,
  set,
  findIndex,
  sortBy,
  flattenDeep,
  isFunction,
} from "lodash"
import ReactResizeDetector from "react-resize-detector"
import { PUT_OR_CALL, SECURITY_TYPE } from "config/const"
import { APIResponse } from "redux/apis/types"
import {
  TrustTableColumnListType,
  TrustTableColumnTitleType,
  judgeHeatMapColor,
} from "config/table_columns"
import * as Formatter from "utils/Formatter"
import { checkIsSp } from "utils/util"
import PushSymbol from "components/molecules/pushSymbol"
import { GetTicketKey } from "node-dist/nodeDistFlag"
import { useDrag, useDrop, DropTargetMonitor } from "react-dnd"
import update from "immutability-helper"
import { XYCoord } from "dnd-core"

// 基本classNameはFunctionにしない
type tableProps = {
  list: Array<object>
  data: Array<object> | Array<Array<number | string>> | []
  className?: Function | string
  onClick?: Function
  onSort?: Function
  sortTarget?: { target: string; type: string }
  spFilter?: Array<string>
  active?: number
}
type emptyTableProps = {
  list: Array<object>
  className?: Function | string
}
// 基本classNameはFunctionにしない
type multiTableProps = {
  list: Array<Array<object>>
  data: Array<object> | []
  className?: Function | string
  onClick?: Function
  scrollable?: boolean
  active?: number
}

export type tableInformation = {
  title?: Function | string
  className?: Function | string
  format?: Function | string
  displaySp?: boolean
  sortable?: boolean
  row?: number
  col?: number
}

type tableArray = {
  list: Array<tableInformation>
  current?: number
  changeCurrent?: (bool: boolean) => void
  first?: number
  last?: number
  onSort?: Function
  sortTarget?: { target: string; type: string }
  isSp?: boolean
  spFilter?: Array<string>
}

type tableBody = tableArray & {
  id?: number
  data: object
  dataList?: Array<object> | []
  onClick?: Function
  index?: number
  active?: number
  current?: number
  moveRow?: (dragIndex: number, hoverIndex: number) => void
  hoverDataNum?: number
  setHoverDataNum?: Function
  setDropFlag?: Function
  addClass?: string
}

type multiTableBody = {
  id?: number
  list: Array<Array<tableInformation>>
  data: object
  dataList?: Array<object> | []
  onClick?: Function
  index?: number
  active?: number
  moveRow?: (dragIndex: number, hoverIndex: number) => void
  hoverDataNum?: number
  setHoverDataNum?: Function
  setDropFlag?: Function
}

type ColumnListType = {
  id?: number
} & tableInformation

const recreateColumnList: (
  list: Array<tableInformation>
) => Array<ColumnListType> = list =>
  list.map((row: tableInformation, key: number) => set(row, "id", key))
const filterColumnList: (
  list: Array<ColumnListType>
) => Array<ColumnListType> = list =>
  list.filter((row: ColumnListType) => !row.displaySp)

const TableHead = (
  props: tableArray & {
    variableTitle?: string
    clearColumn?: boolean
    index?: number
  }
) => (
  <tr>
    {props.list.map((row, i: number) =>
      includes(props.spFilter, row.className) && props.isSp ? null : (
        <th
          key={i}
          className={ClassNames(
            typeof row.className === "string"
              ? row.className
              : row.className(row),
            { active: row.displaySp || i === props.current }
          )}
          rowSpan={row.row}
          colSpan={row.col}
          onClick={() => {
            if (row.sortable && props.onSort) {
              props.onSort(row)
            }
          }}
        >
          {i !== props.current ? (
            !row.title || typeof row.title === "string" ? (
              row.title
            ) : (
              row.title(props)
            )
          ) : (
            <>
              <i
                className={ClassNames("material-icons prev", {
                  disabled: props.current === props.first,
                })}
                onClick={() =>
                  props.current !== props.first && props.changeCurrent(false)
                }
              >
                chevron_left
              </i>
              {!row.title || typeof row.title === "string"
                ? row.title
                : row.title()}
              <i
                className={ClassNames("material-icons next", {
                  disabled: props.current === props.last,
                })}
                onClick={() =>
                  props.current !== props.last && props.changeCurrent(true)
                }
              >
                chevron_right
              </i>
            </>
          )}
        </th>
      )
    )}
    {props.clearColumn && !props.index && (
      <th className="clear-column" rowSpan={2} />
    )}
  </tr>
)

const TableBody = (props: tableBody) => (
  <tr
    key={props.index}
    className={ClassNames(props.addClass, {
      active: props.active === props.index,
    })}
  >
    {props.list.map((row, i: number) =>
      includes(props.spFilter, row.className) && props.isSp ? null : (
        <td
          key={i}
          className={ClassNames(
            typeof row.className === "string"
              ? row.className
              : row.className(props.data, props.index),
            { active: row.displaySp || i === props.current }
          )}
          onClick={() =>
            props.onClick && props.onClick(props.data, props.index, row)
          }
          title={get(props.data, "DisplayName")}
        >
          {!row.format || typeof row.format === "string"
            ? row.format
            : row.format(props.data, props.index)}
        </td>
      )
    )}
  </tr>
)

const DraggableTableBody = (props: tableBody) => {
  const ref = useRef<HTMLTableRowElement>(null)

  const [{ dropResult }, drop] = useDrop({
    accept: "row",
    collect: (monitor: any) => ({
      dropResult: monitor.getDropResult(),
    }),
    hover(
      item: {
        index: number
        id: string
        type: string
      },
      monitor: DropTargetMonitor
    ) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = props.index

      //位置が変わらなければ何もしない
      if (dragIndex === hoverIndex) {
        return
      }

      // 座標取得
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      //　中央取得
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // マウス位置確定
      const clientOffset = monitor.getClientOffset()

      //　上方向のpx取得
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // マウスの高さが行の半分を超えたら移動を実行
      // 下方向にドラッグする場合、カーソルが50％未満の場合にのみ移動
      // 上方向にドラッグする場合、カーソルが50％を超えたの場合にのみ移動

      //下方向にドラッグ
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // 上方向にドラッグ
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // ここで実行
      props.moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  // console.log(dropResult)

  //isDraggingでドラッグ
  const [{ isDragging }, drag] = useDrag({
    item: { type: "row", id: props.id, index: props.index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = props.hoverDataNum === props.index ? 0 : 1
  drag(drop(ref))

  useEffect(() => {
    if (!isDragging) {
      props.setHoverDataNum(null)
    }
  }, [isDragging])

  useEffect(() => {
    if (!dropResult) return
    props.setDropFlag(true)
  }, [dropResult])

  return (
    <tr
      ref={ref}
      key={props.index}
      style={{ opacity }}
      className={ClassNames({
        active: props.active === props.index,
        isDragging: isDragging,
      })}
    >
      {props.list.map((row, i: number) =>
        includes(props.spFilter, row.className) && props.isSp ? null : (
          <td
            key={i}
            className={ClassNames(
              typeof row.className === "string"
                ? row.className
                : row.className(props.data, props.index),
              { active: row.displaySp || i === props.current }
            )}
            onClick={() =>
              props.onClick && props.onClick(props.data, props.index)
            }
          >
            {!row.format || typeof row.format === "string"
              ? row.format
              : row.format(props.data, props.index)}
          </td>
        )
      )}
    </tr>
  )
}

const DraggableMultiLineTableBody: React.FC<multiTableBody> = props => {
  const ref = useRef(null)

  const [{ dropResult }, drop] = useDrop({
    accept: "row",
    collect: (monitor: any) => ({
      dropResult: monitor.getDropResult(),
    }),
    hover(
      item: {
        index: number
        id: string
        type: string
      },
      monitor: DropTargetMonitor
    ) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = props.index

      //位置が変わらなければ何もしない
      if (dragIndex === hoverIndex) {
        return
      }

      // 座標取得
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      //　中央取得
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // マウス位置確定
      const clientOffset = monitor.getClientOffset()

      //　上方向のpx取得
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // マウスの高さが行の半分を超えたら移動を実行
      // 下方向にドラッグする場合、カーソルが50％未満の場合にのみ移動
      // 上方向にドラッグする場合、カーソルが50％を超えたの場合にのみ移動

      //下方向にドラッグ
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // 上方向にドラッグ
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // ここで実行
      props.moveRow(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  //isDraggingでドラッグ
  const [{ isDragging }, drag] = useDrag({
    item: { type: "row", id: props.id, index: props.index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const opacity = props.hoverDataNum === props.index ? 0 : 1
  drag(drop(ref))

  useEffect(() => {
    if (!isDragging) {
      props.setHoverDataNum(null)
    }
  }, [isDragging])

  useEffect(() => {
    if (!dropResult) return
    props.setDropFlag(true)
  }, [dropResult])

  return (
    <>
      {props.list.map((li, i) => (
        <tr
          ref={i === 0 ? ref : null}
          style={{ opacity }}
          key={i}
          className={ClassNames({
            active: props.active === props.index,
            "list-row": i === 0,
          })}
        >
          {li.map((row, i: number) => (
            <td
              key={i}
              className={ClassNames(
                typeof row.className === "string"
                  ? row.className
                  : row.className(props.data, props.index)
              )}
              onClick={() =>
                props.onClick && props.onClick(props.data, props.index)
              }
              rowSpan={row.row}
              colSpan={row.col}
            >
              {!row.format || typeof row.format === "string"
                ? row.format
                : row.format(props.data, props.index)}
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}
const MultiLineTableBody: React.FC<multiTableBody> = props => (
  <>
    {props.list.map((li, i) => (
      <tr
        key={i}
        className={ClassNames({
          active: props.active === props.index,
          "list-row": i === 0,
        })}
      >
        {li.map((row, i: number) => (
          <td
            key={i}
            className={ClassNames(
              typeof row.className === "string"
                ? row.className
                : row.className(props.data, props.index)
            )}
            onClick={() =>
              props.onClick && props.onClick(props.data, props.index)
            }
            rowSpan={row.row}
            colSpan={row.col}
          >
            {!row.format || typeof row.format === "string"
              ? row.format
              : row.format(props.data, props.index)}
          </td>
        ))}
      </tr>
    ))}
  </>
)

export const BaseTable: React.FC<
  tableProps & {
    noTitleFlag?: boolean
    variableTitle?: string
    addBodyClass?: string
  }
> = props => {
  const columnList: Array<ColumnListType> = recreateColumnList(props.list)
  const changeColumnList: Array<ColumnListType> = filterColumnList(columnList)
  const firstColumn = changeColumnList[0].id
  const lastColumn = changeColumnList[changeColumnList.length - 1].id
  const [current, changeCurrent] = useState<number>(firstColumn)
  const changeCurrentDisplay = (direction: boolean) => {
    const index = findIndex(changeColumnList, { id: current })
    changeCurrent(changeColumnList[index + (direction ? 1 : -1)].id)
  }
  return (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        // const isSpWindowSize = window.innerWidth < 769
        const isSpWindowSize = window.innerWidth < 561
        return (
          <table className={ClassNames("table", props.className)}>
            {!props.noTitleFlag && (
              <thead>
                <TableHead
                  list={props.list}
                  current={current}
                  changeCurrent={(bool: boolean) => changeCurrentDisplay(bool)}
                  first={firstColumn}
                  last={lastColumn}
                  spFilter={props.spFilter}
                  isSp={isSpWindowSize}
                  onSort={props.onSort}
                  sortTarget={props.sortTarget}
                  variableTitle={props.variableTitle}
                />
              </thead>
            )}
            <tbody>
              {map(props.data, (row: object, i: number) => (
                <TableBody
                  index={i}
                  list={props.list}
                  data={row}
                  onClick={props.onClick}
                  current={current}
                  spFilter={props.spFilter}
                  isSp={isSpWindowSize}
                  active={props.active}
                  addClass={props.addBodyClass}
                />
              ))}
            </tbody>
          </table>
        )
      }}
    />
  )
}

export const EmptyTable: React.FC<emptyTableProps> = props => {
  const columnList: Array<ColumnListType> = recreateColumnList(props.list)
  const changeColumnList: Array<ColumnListType> = filterColumnList(columnList)
  const firstColumn = changeColumnList[0].id
  const lastColumn = changeColumnList[changeColumnList.length - 1].id
  const [current, changeCurrent] = useState<number>(firstColumn)
  const changeCurrentDisplay = (direction: boolean) => {
    const index = findIndex(changeColumnList, { id: current })
    changeCurrent(changeColumnList[index + (direction ? 1 : -1)].id)
  }
  return (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        const isSpWindowSize = window.innerWidth < 561
        return (
          <div className="empty-area">
            <table className={ClassNames("table", props.className)}>
              <thead>
                <TableHead
                  list={props.list}
                  current={current}
                  changeCurrent={(bool: boolean) => changeCurrentDisplay(bool)}
                  first={firstColumn}
                  last={lastColumn}
                  isSp={isSpWindowSize}
                />
              </thead>
            </table>
            <div className="contents-empty">
              <span>データはありません</span>
            </div>
          </div>
        )
      }}
    />
  )
}

//国内株＞銘柄情報＞合併・株式交換用
export const UnitChangeTable: React.FC<multiTableProps> = props => {
  const createTableRow: Array<object> = sortBy(
    filter(flattenDeep(props.list), (data: object) => {
      return isFunction(get(data, "format"))
    }),
    "dataNum"
  )

  const tableBody = (
    <table className={ClassNames("table", props.className)}>
      <tbody>
        {map(props.data, (row: object, i) => (
          <TableBody
            key={i}
            index={i}
            list={createTableRow}
            data={row}
            onClick={props.onClick}
          />
        ))}
      </tbody>
    </table>
  )
  return (
    <>
      <table className={ClassNames("table table-header", props.className)}>
        <colgroup>
          {map(createTableRow, (data: object) => (
            <col className={get(data, "className")} />
          ))}
        </colgroup>
        <thead>
          {props.list.map((li, i) => (
            <TableHead key={i} list={li} />
          ))}
        </thead>
      </table>
      {props.scrollable ? (
        <div className="table-scroll">{tableBody}</div>
      ) : (
        tableBody
      )}
    </>
  )
}
export const MultipleLineTable: React.FC<
  multiTableProps & {
    active?: number
    emptyText?: string
    draggable?: boolean
    setData?: Function
    setDropFlag?: Function
  }
> = props => {
  const [hoverDataNum, setHoverDataNum] = useState<number | null>(null)

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!props.draggable) return
      const dragRow = props.data[dragIndex]
      const updateData = update(props.data, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]],
      })
      setHoverDataNum(hoverIndex)
      props.setData(updateData)
    },
    [props.data]
  )
  const tableBody = (
    <table className={ClassNames("table", props.className)}>
      <tbody>
        {!isEmpty(props.data) ? (
          map(props.data, (row: object, i) =>
            props.draggable ? (
              <DraggableMultiLineTableBody
                key={i}
                index={i}
                id={get(row, "id")}
                list={props.list}
                data={row}
                dataList={props.data}
                active={props.active}
                onClick={props.onClick}
                moveRow={moveRow}
                hoverDataNum={hoverDataNum}
                setHoverDataNum={setHoverDataNum}
                setDropFlag={props.setDropFlag}
              />
            ) : (
              <MultiLineTableBody
                key={i}
                index={i}
                list={props.list}
                data={row}
                active={props.active}
                onClick={props.onClick}
              />
            )
          )
        ) : (
          <div className="empty-text">{props.emptyText}</div>
        )}
      </tbody>
    </table>
  )
  return (
    <>
      <table className={ClassNames("table table-header", props.className)}>
        <thead>
          {props.list.map((li, i) => (
            <TableHead
              key={i}
              list={li}
              clearColumn={props.scrollable}
              index={i}
            />
          ))}
        </thead>
      </table>
      {props.scrollable ? (
        <div className="table-scroll">{tableBody}</div>
      ) : (
        tableBody
      )}
    </>
  )
}

export const SeparateTable: React.FC<
  tableProps & {
    scrollable?: boolean
    active?: number
    scrollID?: string
    variableTitle?: string
    emptyText?: string
    draggable?: boolean
    setData?: Function
    setDropFlag?: Function
  }
> = props => {
  const [hoverDataNum, setHoverDataNum] = useState<number | null>(null)
  const columnList: Array<ColumnListType> = recreateColumnList(props.list)
  const changeColumnList: Array<ColumnListType> = filterColumnList(columnList)
  const firstColumn = changeColumnList[0].id
  const lastColumn = changeColumnList[changeColumnList.length - 1].id
  const [current, changeCurrent] = useState<number>(firstColumn)
  const changeCurrentDisplay = (direction: boolean) => {
    const index = findIndex(changeColumnList, { id: current })
    changeCurrent(changeColumnList[index + (direction ? 1 : -1)].id)
  }
  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!props.draggable) return
      const dragRow = props.data[dragIndex]
      const updateData = update(props.data, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]],
      })
      setHoverDataNum(hoverIndex)
      props.setData(updateData)
    },
    [props.data]
  )
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])

  const tableBody = !isEmpty(props.data) ? (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        // const isSpWindowSize = window.innerWidth < 769
        const isSpWindowSize = window.innerWidth < 561
        return (
          <table
            className={ClassNames("table", props.className)}
            id={props.scrollID && `${props.scrollID}-table`}
          >
            <tbody>
              {map(props.data, (row: object, i) =>
                props.draggable ? (
                  <DraggableTableBody
                    key={i}
                    index={i}
                    id={get(row, "id")}
                    list={props.list}
                    data={row}
                    dataList={props.data}
                    active={props.active}
                    current={current}
                    onClick={props.onClick}
                    spFilter={props.spFilter}
                    isSp={isSpWindowSize}
                    moveRow={moveRow}
                    hoverDataNum={hoverDataNum}
                    setHoverDataNum={setHoverDataNum}
                    setDropFlag={props.setDropFlag}
                  />
                ) : (
                  <TableBody
                    key={i}
                    index={i}
                    list={props.list}
                    data={row}
                    active={props.active}
                    current={current}
                    onClick={props.onClick}
                    spFilter={props.spFilter}
                    isSp={isSpWindowSize}
                  />
                )
              )}
            </tbody>
          </table>
        )
      }}
    />
  ) : (
    <div className="empty-text">{props.emptyText || ""}</div>
  )
  return (
    <ReactResizeDetector
      handleWidth={true}
      render={() => {
        // const isSpWindowSize = window.innerWidth < 769
        const isSpWindowSize = window.innerWidth < 561
        return (
          <>
            <table
              className={ClassNames(
                "table table-header separateTable",
                props.className
              )}
            >
              <thead>
                <TableHead
                  list={props.list}
                  current={current}
                  changeCurrent={(bool: boolean) => changeCurrentDisplay(bool)}
                  first={firstColumn}
                  last={lastColumn}
                  clearColumn={props.scrollable}
                  spFilter={props.spFilter}
                  isSp={isSpWindowSize}
                  onSort={props.onSort}
                  sortTarget={props.sortTarget}
                  variableTitle={props.variableTitle}
                />
              </thead>
            </table>
            {/* spにテーブル内スクロールは必要ない（悪さするので、外す）。 */}
            <div
              className={ClassNames({ "table-scroll": !isSp })}
              id={props.scrollID}
            >
              {tableBody}
            </div>
          </>
        )
      }}
    />
  )
}

type List = {
  title?: string
  value: Function
  unit?: Function
}

type ObjectType = {
  titleCls: string
  bodyCls: string
  list: Array<Array<object>>
}

export type InfoTableProps = {
  list: ObjectType
  data: object
  pushData?: object
  className?: string
  wrapperCls?: string
  onClick?: Function
}

export const InfoTable: React.FC<InfoTableProps> = props => {
  const { titleCls, bodyCls, list } = props.list
  return (
    <div className={props.wrapperCls}>
      {map(list, (ls, index) => (
        <table key={index} className={ClassNames("table", props.className)}>
          <tbody>
            {map(ls, (row: List, i) => (
              <tr
                key={i}
                onClick={e =>
                  props.onClick && props.onClick(e, row, props.data)
                }
              >
                <th className={titleCls}>{row.title}</th>
                <td className={bodyCls}>
                  {row.value(props.data, props.pushData)}
                  {row.unit && row.unit(props.data)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  )
}

export const SignTable = (props: {
  buy: Array<object>
  sell: Array<object>
}) => {
  return (
    <table className="table table-board-order table-striped">
      <thead>
        <tr className="title-row">
          <th className="sell-cell title-cell">売気配数量</th>
          <th className="mark-cell" />
          <th className="center-cell">気配値</th>
          <th className="mark-cell" />
          <th className="buy-cell title-cell">買気配数量</th>
        </tr>
        <tr className="nariyuki-row">
          <th className="number-cell">8,100</th>
          <th className="mark-cell" />
          <th className="center-cell">成行</th>
          <th className="mark-cell" />
          <th className="number-cell">6,900</th>
        </tr>
        <tr className="over-row">
          <th className="number-cell">6,564,900</th>
          <th className="mark-cell" />
          <th className="center-cell">OVER</th>
          <th className="mark-cell" />
          <th className="number-cell" />
        </tr>
      </thead>
      <tbody>
        {map(props.sell, (row, i) => {
          return (
            <tr key={i}>
              <td className="buy-sell-price-cell sell-cell number-cell">
                <span
                  className={ClassNames({
                    sell: i === 4,
                  })}
                >
                  700
                </span>
              </td>
              <td className="mark-cell ">
                <span className="sell">
                  {i === size(props.sell) - 1 ? "・" : ""}
                </span>
              </td>
              <td className="center-cell">
                <span className="buy">579.7</span>
              </td>
              <td className="mark-cell" />
              <td className="buy-sell-price-cell buy-cell number-cell" />
            </tr>
          )
        })}
        <tr className="current-price-center-row">
          <td className="buy-sell-price-cell sell-cell number-cell">現在値</td>
          <td className="mark-cell">〇</td>
          <td className="center-cell">575.3</td>
          <td className="mark-cell" />
          <td className="buy-sell-price-cell buy-cell number-cell" />
        </tr>
        {map(props.buy, (row, i) => {
          return (
            <tr key={i}>
              <td className="buy-sell-price-cell sell-cell number-cell" />
              <td className="mark-cell" />
              <td className="center-cell ">
                <span className="sell">576.7</span>
              </td>
              <td className="mark-cell ">
                <span className="buy">{i === 0 ? "・" : ""}</span>
              </td>
              <td className="buy-sell-price-cell sell-cell number-cell">
                <span
                  className={ClassNames({
                    buy: i === 0,
                  })}
                >
                  700
                </span>
              </td>
            </tr>
          )
        })}
      </tbody>
      <tfoot>
        <tr className="under-row">
          <th className="buy-sell-price-cell number-cell" />
          <th className="mark-cell" />
          <th className="center-cell">UNDER</th>
          <th className="mark-cell" />
          <th className="buy-sell-price-cell number-cell">5,036,100</th>
        </tr>
      </tfoot>
    </table>
  )
}

export const RowTable = (props: {
  list: Array<{
    title: string
    value: string | Function
    className?: string
    unit?: string | Function
  }>
  data?: APIResponse.Res
  tableCls?: string
  titleCls?: string
  unitCls?: string
}) => {
  return (
    <table className={ClassNames("table", props.tableCls)}>
      <tbody>
        {props.list.map((row, i) => (
          <tr key={i}>
            <th className={props.titleCls}>{row.title}</th>
            <td className={typeof row.className === "string" && row.className}>
              {typeof row.value === "function" ? (
                row.value(props.data)
              ) : (
                <span>{row.value}</span>
              )}
              {typeof row.unit === "string" ||
              typeof row.unit === "function" ? (
                <span className={ClassNames("unit", props.unitCls)}>
                  {typeof row.unit === "function" ? row.unit() : row.unit}
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

type CreditTableType = {
  data?: APIResponse.Res
}
export const CreditTable: React.FC<CreditTableType> = props => {
  return (
    <table className="table table-striped table-border">
      <thead>
        <tr>
          <th className="type-cell">貸借</th>
          <th className="">信用残</th>
          <th className="">前週比</th>
          <th className="">倍率</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th className="type-cell">売</th>
          <td className="number-cell">
            {Formatter.number(get(props.data, "GoukeiUriZan"))}
          </td>
          <td className="number-cell">
            <span
              className={Formatter.upDown(get(props.data, "GoukeiUriZanZenhi"))}
            >
              {Formatter.number(
                get(props.data, "GoukeiUriZanZenhi"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(props.data, "GoukeiShinyoBairitsu"), 2)}
          </td>
        </tr>
        <tr>
          <th className="type-cell">買</th>
          <td className="number-cell">
            {Formatter.number(get(props.data, "GoukeiKaiZan"))}
          </td>
          <td className="number-cell">
            <span
              className={Formatter.upDown(get(props.data, "GoukeiKaiZanZenhi"))}
            >
              {Formatter.number(
                get(props.data, "GoukeiKaiZanZenhi"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell" />
        </tr>
      </tbody>
    </table>
  )
}

export const CompanyOverview = (props: {
  list: Array<Array<{ title: string; value: Function; col?: number }>>
  data: APIResponse.Res
}) => {
  return (
    <table className="table table-striped table-index-detail">
      <tbody>
        {props.list.map((row, i) => (
          <tr key={i} className={size(row) === 1 ? "border-row" : ""}>
            {row.map((val, j) => (
              <React.Fragment key={j}>
                <th>{val.title}</th>
                <td colSpan={val.col ? val.col : 1}>{val.value(props.data)}</td>
              </React.Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

type HeatMapList = {
  data?: APIResponse.Res
  onClick?: Function
  selectedData?: APIResponse.Res
  removeSymbolcode?: boolean
}
export const HeatMapList: React.FC<HeatMapList> = props => {
  const resultList = get(props.data, "ResultList", [])
  const currentIndex = findIndex(resultList, {
    Symbol: get(props.selectedData, "Symbol"),
  })
  return (
    <ul className="heat-map-list">
      {map(resultList, (row, i: number) => {
        // 使用するデータ群
        const ChangedRatio = get(row, "ChangedRatio", null)
        const Price = get(row, "Price", "")
        const NumberOfDecimals = get(row, "NumberOfDecimals", 0)
        const PriceTime = get(row, "PriceTime", "")
        const ChangedPrice = get(row, "ChangedPrice", "")

        return (
          <li
            key={i}
            className={ClassNames(judgeHeatMapColor(ChangedRatio), {
              active: i === currentIndex,
            })}
            onClick={() => props.onClick && props.onClick(row, i)}
          >
            <div className="heat-map-symbol-name-col">
              {!props.removeSymbolcode && (
                <div className="heat-map-code">{get(row, "Symbol", "")}</div>
              )}
              <div className="heat-map-symbol-name">
                {get(row, "SymbolNameS", "") || get(row, "SymbolName", "")}
              </div>
            </div>

            <div className="heat-map-list-col">
              <div className="heat-map-list-col-left">
                <div className="heat-map-currency-price">
                  {Formatter.number(Price, NumberOfDecimals)}
                </div>

                <div className="heat-map-volume-row">
                  <div className="time-col">
                    {Formatter.date(PriceTime, "HH:mm")}
                  </div>
                  {/*<div className="heat-map-volume-col">*/}
                  {/*  <span className="heat-map-volume-title">出来高</span>*/}
                  {/*  <span className="heat-map-volume">*/}
                  {/*    {Formatter.number(get(row, "TradVolume", ""))}*/}
                  {/*  </span>*/}
                  {/*</div>*/}
                </div>
              </div>

              <div className="heat-map-change-price-col heat-map-list-col-right">
                <div className="heat-map-change-price">
                  {Formatter.number(
                    ChangedPrice,
                    NumberOfDecimals,
                    "floor",
                    true
                  )}
                </div>
                <div className="heat-map-change-ratio">
                  {Formatter.number(ChangedRatio, 2, "floor", true)}
                  <span className="unit">%</span>
                </div>
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

type HeatMapSymbolPartsType = {
  data: object
  className: Function | string
  format: Function | string
  index: number
  onClick?: Function
  selectedSymbol: string
  changeSymbol: Function
  activeSymbol: APIResponse.Res
}
const HeatMapSymbolParts: React.FC<HeatMapSymbolPartsType> = props => {
  const name =
    !props.format || typeof props.format === "string"
      ? props.format
      : props.format(props.data, props.index)
  const setPosition = (e: React.MouseEvent<HTMLDivElement>) => {
    const clientRect = e.currentTarget.getBoundingClientRect()
    if (name === props.selectedSymbol) return
    props.changeSymbol({
      name: name,
      position: [
        clientRect.top + Number(e.currentTarget.clientHeight),
        clientRect.left,
      ],
    })
  }
  return (
    <td
      className={ClassNames(
        typeof props.className === "string"
          ? props.className
          : props.className(props.data, props.index),
        {
          active:
            get(props.data, "Symbol") === get(props.activeSymbol, "Symbol"),
        }
      )}
      onClick={() => props.onClick && props.onClick(props.data, props.index)}
      onMouseEnter={e => setPosition(e)}
      onMouseMove={e => setPosition(e)}
      onMouseLeave={() => {
        props.changeSymbol({
          name: "",
          position: [0, 0],
        })
      }}
    >
      {name}
    </td>
  )
}

type ShowSymbol = {
  name: string
  position: [number, number]
}
export const HeatMapSymbolList: React.FC<
  tableProps & {
    scrollable?: boolean
    active?: number
    selectedData?: APIResponse.Res
  }
> = props => {
  const [showSymbol, changeSymbol] = useState<ShowSymbol>({
    name: "",
    position: [0, 0],
  })
  const chunkDataList = chunk(
    props.data,
    Math.ceil(props.data.length / props.list.length)
  )
  const tableBody = (
    <table className={ClassNames("table", props.className)}>
      <tbody>
        {map(chunkDataList[0], (dataList, i: number) => (
          <tr key={i}>
            {props.list.map((li: tableInformation, j: number) => (
              <HeatMapSymbolParts
                key={j}
                data={chunkDataList[j][i]}
                className={li.className}
                format={li.format}
                index={j}
                onClick={props.onClick}
                selectedSymbol={showSymbol.name}
                changeSymbol={changeSymbol}
                activeSymbol={props.selectedData}
              />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
  return (
    <>
      <table className={ClassNames("table table-header", props.className)}>
        <thead>
          <TableHead list={props.list} />
        </thead>
      </table>
      {props.scrollable ? (
        <div className="table-scroll">{tableBody}</div>
      ) : (
        tableBody
      )}
    </>
  )
}

const isSP = checkIsSp()
export const OptionTable = (props: {
  call: APIResponse.Res
  put: APIResponse.Res
  stk: Array<number>
  onClick?: Function
  active?: number
  activePutOrCall: number
  callSymbolDetail?: Function
  isModal?: boolean
}) => {
  const _onClick = (symbol: any, index: number) => {
    if (!props.onClick) return
    props.onClick(symbol, index)
  }
  const [isShowCol, setIsShowCol] = useState(isSP ? [0] : [0, 1, 2])
  const [pages, setPages] = useState(0)
  const [viewList, setViewList] = useState([])
  const [putList, setPutList] = useState([])
  const [callList, setCallList] = useState([])
  const [startFromAtm, atmFromEnd, rowCount] = useMemo(() => {
    // ATMを中心に、上下何行表示するか？
    const ROW = !props.isModal ? 4 : 7
    const START = -ROW
    const END = ROW + 1
    const ONE_PAGE_ROW_COUNT = ROW * 2 + 1
    return [START, END, ONE_PAGE_ROW_COUNT]
  }, [])
  const atmIndex = useMemo(() => {
    return Number(findKey(props.call, "ATMFlag"))
  }, [props.call, props.put, props.stk])
  const [firstIndex, endIndex] = useMemo(() => {
    const firstIndex = atmIndex + startFromAtm
    const endIndex = atmIndex + atmFromEnd
    return [firstIndex, endIndex]
  }, [atmIndex])
  const [_firstIndex, setFirstIndex] = useState(firstIndex)

  const [minPage, maxPage] = useMemo(() => {
    const maxlength = props.stk.length
    const minPage = Math.ceil((maxlength - endIndex) / rowCount)
    const maxPage = Math.ceil((maxlength - firstIndex) / rowCount)
    return [minPage, -maxPage]
  }, [props.stk])
  const cheackLength = (pages: number) => {
    if (pages) {
      const maxlength = props.stk.length
      let _firstIndex =
        firstIndex + pages * rowCount > 0 ? firstIndex + pages * rowCount : 0
      // _firstIndexが0の場合、rowCount(9)を、endIndexが、配列の最大値の場合,最大値を、それ以外の場合、次のPageに対応する値を代入する。
      // _firstIndexが必ずしもピッタリ0になるとは限らないため、純粋にendIndexからマイナスしていけばいいわけではないので下記のようになっている。
      const _endIndex =
        _firstIndex === 0
          ? rowCount
          : endIndex + pages * rowCount <= maxlength
          ? endIndex + pages * rowCount
          : maxlength
      // 上記と同じように、_endIndexが必ずしもピッタリ配列の最大値になるとは限らないため、純粋にfirstIndexにプラスしていけばいいわけではないので、最大値の場合、正しい値を再代入。
      if (_endIndex === maxlength) _firstIndex = maxlength - rowCount
      return [_firstIndex, _endIndex]
    }
    if (pages === 0) return [firstIndex, endIndex]
  }
  useEffect(() => {
    const [_firstIndex, _endIndex] = cheackLength(pages)
    setFirstIndex(_firstIndex)
    setViewList(props.stk.slice(_firstIndex, _endIndex))
    setPutList(props.put.slice(_firstIndex, _endIndex))
    setCallList(props.call.slice(_firstIndex, _endIndex))
  }, [pages])

  useEffect(() => {
    if (isEmpty(props.stk) || isEmpty(props.put) || isEmpty(props.call)) return
    setFirstIndex(firstIndex)
    setViewList(props.stk.slice(firstIndex, endIndex))
    setPutList(props.put.slice(firstIndex, endIndex))
    setCallList(props.call.slice(firstIndex, endIndex))
  }, [props.stk])

  useEffect(() => {
    props.callSymbolDetail(putList[0])
  }, [callList])

  const setNextCol = () => {
    let nextColIndex = isShowCol[0]
    nextColIndex = nextColIndex < 2 ? nextColIndex + 1 : 0
    setIsShowCol([nextColIndex])
  }

  return (
    <>
      {!props.isModal ? (
        <div className="table-option-wrapper">
          <table className="table table-striped table-option call">
            <thead>
              <tr>
                <th className="title-call-cell" colSpan={2 + isShowCol.length}>
                  {isSP && (
                    <span
                      onClick={setNextCol}
                      className="material-icons arrow_left"
                    >
                      &#xe037;
                    </span>
                  )}
                  コール
                </th>
              </tr>
              <tr>
                {includes(isShowCol, 2) && (
                  <th className="option-cell iv">IV</th>
                )}
                {includes(isShowCol, 1) && (
                  <th className="option-cell">
                    始値
                    <br />
                    高値
                    <br />
                    安値
                  </th>
                )}
                {includes(isShowCol, 0) && (
                  <th className="option-cell">
                    売気配
                    <br />
                    数量
                  </th>
                )}
                <th className="option-cell">
                  買気配
                  <br />
                  数量
                </th>
                <th className="option-cell">
                  現在値
                  <br />
                  前日比
                  <br />
                  出来高
                </th>
              </tr>
            </thead>
            <tbody>
              {map(callList, (r, index) => {
                return (
                  <tr
                    key={index}
                    onClick={() => {
                      _onClick(r, index)
                    }}
                    className={ClassNames({
                      active:
                        props.active === index &&
                        PUT_OR_CALL.CALL.id === props.activePutOrCall,
                    })}
                  >
                    {includes(isShowCol, 2) && (
                      <td className="number-cell">
                        {Formatter.number(get(r, "Volatility"), 2)}
                      </td>
                    )}
                    {includes(isShowCol, 1) && (
                      <td className="number-cell">
                        <div>{Formatter.number(get(r, "OpenPrice"))}</div>
                        <div>{Formatter.number(get(r, "HighPrice"))}</div>
                        <div>{Formatter.number(get(r, "LowPrice"))}</div>
                      </td>
                    )}
                    {includes(isShowCol, 0) && (
                      <td className="number-cell">
                        <div className={ClassNames({ down: get(r, "Ask") })}>
                          {Formatter.number(get(r, "Ask"), 0, "floor")}
                        </div>
                        <div>{Formatter.number(get(r, "AskVolume"))}</div>
                      </td>
                    )}
                    <td className="number-cell">
                      <div className={ClassNames({ up: get(r, "Bid") })}>
                        {Formatter.number(get(r, "Bid"), 0, "floor")}
                      </div>
                      <div>{Formatter.number(get(r, "BidVolume"))}</div>
                    </td>
                    <td className="number-cell">
                      <div>{Formatter.number(get(r, "Price"))}</div>
                      <div
                        className={ClassNames(
                          Formatter.upDown(get(r, "ChangedPrice"))
                        )}
                      >
                        {Formatter.number(
                          get(r, "ChangedPrice"),
                          0,
                          "floor",
                          true
                        )}
                      </div>
                      <div>{Formatter.number(get(r, "TradVolume"))}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <table className="table table-striped table-option center">
            <thead>
              <tr>
                <th>大阪</th>
              </tr>
              <tr>
                <th className="option-cell">
                  <div
                    className="btn btn-primary btn-circle"
                    onClick={() => {
                      if (maxPage >= pages) return
                      setPages(pages - 1)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_up</i>
                  </div>
                  <div
                    className="btn btn-primary btn-circle"
                    onClick={() => {
                      if (pages >= minPage) return
                      setPages(pages + 1)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_down</i>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {map(viewList, (r: number, i: number) => {
                return (
                  <tr key={i}>
                    <td className="quotes-cell">
                      <div>{Formatter.number(r)}</div>
                      {props.stk[atmIndex] === r && (
                        <div className="up">ATM</div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <table className="table table-striped table-option put">
            <thead>
              <tr>
                <th className="title-put-cell" colSpan={2 + isShowCol.length}>
                  プット
                  {isSP && (
                    <span
                      onClick={setNextCol}
                      className="material-icons arrow_right"
                    >
                      &#xe037;
                    </span>
                  )}
                </th>
              </tr>
              <tr>
                <th className="option-cell">
                  現在値
                  <br />
                  前日比
                  <br />
                  出来高
                </th>
                <th className="option-cell">
                  買気配
                  <br />
                  数量
                </th>
                {includes(isShowCol, 0) && (
                  <th className="option-cell">
                    売気配
                    <br />
                    数量
                  </th>
                )}
                {includes(isShowCol, 1) && (
                  <th className="option-cell">
                    始値
                    <br />
                    高値
                    <br />
                    安値
                  </th>
                )}
                {includes(isShowCol, 2) && (
                  <th className="option-cell iv">IV</th>
                )}
              </tr>
            </thead>
            <tbody>
              {map(putList, (r, index) => {
                return (
                  <tr
                    key={index}
                    className={ClassNames({
                      active:
                        props.active === index &&
                        PUT_OR_CALL.PUT.id === props.activePutOrCall,
                    })}
                    onClick={() => {
                      _onClick(r, index)
                    }}
                  >
                    <td className="number-cell">
                      <div>{Formatter.number(get(r, "Price"))}</div>
                      <div
                        className={ClassNames(
                          Formatter.upDown(get(r, "ChangedPrice"))
                        )}
                      >
                        {Formatter.number(
                          get(r, "ChangedPrice"),
                          0,
                          "floor",
                          true
                        )}
                      </div>

                      <div>{Formatter.number(get(r, "TradVolume"))}</div>
                    </td>
                    <td className="number-cell">
                      <div className={ClassNames({ up: get(r, "Bid") })}>
                        {Formatter.number(get(r, "Bid"), 0, "floor")}
                      </div>
                      <div>{Formatter.number(get(r, "BidVolume"))}</div>
                    </td>
                    {includes(isShowCol, 0) && (
                      <td className="number-cell">
                        <div className={ClassNames({ down: get(r, "Ask") })}>
                          {Formatter.number(get(r, "Ask"), 0, "floor")}
                        </div>
                        <div>{Formatter.number(get(r, "AskVolume"))}</div>
                      </td>
                    )}
                    {includes(isShowCol, 1) && (
                      <td className="number-cell">
                        <div>{Formatter.number(get(r, "OpenPrice"))}</div>
                        <div>{Formatter.number(get(r, "HighPrice"))}</div>
                        <div>{Formatter.number(get(r, "LowPrice"))}</div>
                      </td>
                    )}
                    {includes(isShowCol, 2) && (
                      <td className="number-cell">
                        <div>{Formatter.number(get(r, "Volatility"), 2)}</div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className={ClassNames("table-option-wrapper", {
            isModal: props.isModal,
          })}
        >
          <table className="table table-striped table-option call">
            <thead>
              <tr>
                <th className="title-call-cell">コール</th>
              </tr>
            </thead>
            <tbody>
              {map(callList, (r, index) => {
                const call = props.call[_firstIndex + index]
                return (
                  <tr
                    key={index}
                    onClick={() => {
                      _onClick(r, index)
                    }}
                    className={ClassNames({
                      active:
                        props.active === index &&
                        PUT_OR_CALL.CALL.id === props.activePutOrCall,
                    })}
                  >
                    <td
                      className={ClassNames("modal-cell", {
                        atm: !!get(call, "ATMFlag", null),
                      })}
                    >
                      <div className="btn btn-primary">
                        <span>選択</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <table className="table table-striped table-option center">
            <thead>
              <tr>
                <th>
                  <div
                    className="btn btn-primary btn-circle"
                    onClick={() => {
                      if (maxPage >= pages) return
                      setPages(pages - 1)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_up</i>
                  </div>
                  <span>大阪</span>
                  <div
                    className="btn btn-primary btn-circle"
                    onClick={() => {
                      if (pages >= minPage) return
                      setPages(pages + 1)
                    }}
                  >
                    <i className="material-icons">keyboard_arrow_down</i>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {map(viewList, (r: number, i: number) => {
                const call = props.call[_firstIndex + i]
                return (
                  <tr key={i}>
                    <td
                      className={ClassNames("quotes-cell", {
                        atm: props.stk[atmIndex] === r,
                      })}
                    >
                      <div>{Formatter.number(r)}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <table className="table table-striped table-option put">
            <thead>
              <tr>
                <th className="title-put-cell">プット</th>
              </tr>
            </thead>
            <tbody>
              {map(putList, (r, index) => {
                const call = props.call[_firstIndex + index]
                return (
                  <tr
                    key={index}
                    className={ClassNames({
                      active:
                        props.active === index &&
                        PUT_OR_CALL.PUT.id === props.activePutOrCall,
                    })}
                    onClick={() => {
                      _onClick(r, index)
                    }}
                  >
                    <td
                      className={ClassNames("modal-cell", {
                        atm: !!get(call, "ATMFlag", null),
                      })}
                    >
                      <div className="btn btn-primary">
                        <span>選択</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

export const TrustBalanceTable: React.FC<{ data?: Array<object> }> = props => {
  const { data } = props
  return (
    <table className="table table-trust-balance">
      <thead>
        <tr>
          <th />
          <th colSpan={4}>合計</th>
          <th colSpan={2}>売残高</th>
          <th colSpan={2}>買残高</th>
          <th>信用倍率</th>
        </tr>
        <tr>
          <th />
          <th>売残高</th>
          <th>前日比較</th>
          <th>買残高</th>
          <th>前日比較</th>
          <th>一般</th>
          <th>制度</th>
          <th>一般</th>
          <th>制度</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="title-col" colSpan={10}>
            合計
          </td>
        </tr>
        <tr>
          <td>株数</td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "GoukeiUriZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[0], "GoukeiUriZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[0], "GoukeiUriZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "GoukeiKaiZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[0], "GoukeiKaiZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[0], "GoukeiKaiZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "IppanUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "SeidoUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "IppanKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "SeidoKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {/*信用残のページは固定で小数第二位までとする*/}
            {Formatter.number(get(data[0], "MarginRatio"), 2)}
          </td>
        </tr>
        <tr>
          <td>金額</td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "GoukeiUriZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[0], "GoukeiUriZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[0], "GoukeiUriZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "GoukeiKaiZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[0], "GoukeiKaiZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[0], "GoukeiKaiZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "IppanUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "SeidoUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "IppanKaiZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[0], "SeidoKaiZanValue"))}
          </td>
          <td className="number-cell">-</td>
        </tr>
        <tr>
          <td className="title-col" colSpan={10}>
            日証金
          </td>
        </tr>
        <tr>
          <td>株数</td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "GoukeiUriZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[1], "GoukeiUriZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[1], "GoukeiUriZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "GoukeiKaiZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[1], "GoukeiKaiZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[1], "GoukeiKaiZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "IppanUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "SeidoUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "IppanKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "SeidoKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {/*信用残のページは固定で小数第二位までとする*/}
            {Formatter.number(get(data[1], "MarginRatio"), 2)}
          </td>
        </tr>
        <tr>
          <td>金額</td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "GoukeiUriZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[1], "GoukeiUriZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[1], "GoukeiUriZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "GoukeiKaiZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[1], "GoukeiKaiZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[1], "GoukeiKaiZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "IppanUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "SeidoUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "IppanKaiZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[1], "SeidoKaiZanValue"))}
          </td>
          <td className="number-cell">-</td>
        </tr>
        <tr>
          <td className="title-col" colSpan={10}>
            中証金
          </td>
        </tr>
        <tr>
          <td>株数</td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "GoukeiUriZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[2], "GoukeiUriZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[2], "GoukeiUriZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "GoukeiKaiZanVolume"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[2], "GoukeiKaiZanZenhiVolume"))
              )}
            >
              {Formatter.number(
                get(data[2], "GoukeiKaiZanZenhiVolume"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "IppanUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "SeidoUriZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "IppanKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "SeidoKaiZanVolume"))}
          </td>
          <td className="number-cell">
            {/*信用残のページは固定で小数第二位までとする*/}
            {Formatter.number(get(data[2], "MarginRatio"), 2)}
          </td>
        </tr>
        <tr>
          <td>金額</td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "GoukeiUriZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[2], "GoukeiUriZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[2], "GoukeiUriZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "GoukeiKaiZanValue"))}
          </td>
          <td className="number-cell">
            <span
              className={ClassNames(
                "",
                Formatter.upDown(get(data[2], "GoukeiKaiZanZenhiValue"))
              )}
            >
              {Formatter.number(
                get(data[2], "GoukeiKaiZanZenhiValue"),
                0,
                "floor",
                true
              )}
            </span>
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "IppanUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "SeidoUriZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "IppanKaiZanValue"))}
          </td>
          <td className="number-cell">
            {Formatter.number(get(data[2], "SeidoKaiZanValue"))}
          </td>
          <td className="number-cell">-</td>
        </tr>
        <tr>
          <td colSpan={10}>
            <span className="emphasis">※ 単位: 1000株 100万円</span>
          </td>
        </tr>
      </tbody>
    </table>
  )
}
export const TrustBalanceTableSP: React.FC<{
  data?: Array<object>
}> = props => {
  const { data } = props
  return (
    <div>
      <table className="table table-trust-balance">
        <thead>
          <tr>
            <th />
            <th colSpan={4}>合計</th>
          </tr>
          <tr>
            <th />
            <th>売残高</th>
            <th>前日比較</th>
            <th>買残高</th>
            <th>前日比較</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="title-col" colSpan={5}>
              合計
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "GoukeiUriZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[0], "GoukeiUriZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[0], "GoukeiUriZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "GoukeiKaiZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[0], "GoukeiKaiZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[0], "GoukeiKaiZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "GoukeiUriZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[0], "GoukeiUriZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[0], "GoukeiUriZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "GoukeiKaiZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[0], "GoukeiKaiZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[0], "GoukeiKaiZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td className="title-col" colSpan={5}>
              日証金
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "GoukeiUriZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[1], "GoukeiUriZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[1], "GoukeiUriZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "GoukeiKaiZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[1], "GoukeiKaiZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[1], "GoukeiKaiZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "GoukeiUriZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[1], "GoukeiUriZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[1], "GoukeiUriZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "GoukeiKaiZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[1], "GoukeiKaiZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[1], "GoukeiKaiZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td className="title-col" colSpan={5}>
              中証金
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "GoukeiUriZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[2], "GoukeiUriZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[2], "GoukeiUriZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "GoukeiKaiZanVolume"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[2], "GoukeiKaiZanZenhiVolume"))
                )}
              >
                {Formatter.number(
                  get(data[2], "GoukeiKaiZanZenhiVolume"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "GoukeiUriZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[2], "GoukeiUriZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[2], "GoukeiUriZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "GoukeiKaiZanValue"))}
            </td>
            <td className="number-cell">
              <span
                className={ClassNames(
                  "",
                  Formatter.upDown(get(data[2], "GoukeiKaiZanZenhiValue"))
                )}
              >
                {Formatter.number(
                  get(data[2], "GoukeiKaiZanZenhiValue"),
                  0,
                  "floor",
                  true
                )}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <table className="table table-trust-balance">
        <thead>
          <tr>
            <th />
            <th colSpan={2}>売残高</th>
            <th colSpan={2}>買残高</th>
            <th>信用倍率</th>
          </tr>
          <tr>
            <th />
            <th>一般</th>
            <th>制度</th>
            <th>一般</th>
            <th>制度</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="title-col" colSpan={6}>
              合計
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "IppanUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "SeidoUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "IppanKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "SeidoKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {/*信用残のページは固定で小数第二位までとする*/}
              {Formatter.number(get(data[0], "MarginRatio"), 2)}
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "IppanUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "SeidoUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "IppanKaiZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[0], "SeidoKaiZanValue"))}
            </td>
            <td className="number-cell">-</td>
          </tr>
          <tr>
            <td className="title-col" colSpan={6}>
              日証金
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "IppanUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "SeidoUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "IppanKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "SeidoKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {/*信用残のページは固定で小数第二位までとする*/}
              {Formatter.number(get(data[1], "MarginRatio"), 2)}
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "IppanUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "SeidoUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "IppanKaiZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[1], "SeidoKaiZanValue"))}
            </td>
            <td className="number-cell">-</td>
          </tr>
          <tr>
            <td className="title-col" colSpan={6}>
              中証金
            </td>
          </tr>
          <tr>
            <td>株数</td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "IppanUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "SeidoUriZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "IppanKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "SeidoKaiZanVolume"))}
            </td>
            <td className="number-cell">
              {/*信用残のページは固定で小数第二位までとする*/}
              {Formatter.number(get(data[2], "MarginRatio"), 2)}
            </td>
          </tr>
          <tr>
            <td>金額</td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "IppanUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "SeidoUriZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "IppanKaiZanValue"))}
            </td>
            <td className="number-cell">
              {Formatter.number(get(data[2], "SeidoKaiZanValue"))}
            </td>
            <td className="number-cell">-</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export type MarginBalanceTableType = {
  tableColumns: Array<TrustTableColumnListType>
  data?: object
  className?: string
}

const fetchTableInfo = (list: Array<TrustTableColumnListType>) => {
  const flattenZipArray = (li: Array<Array<TrustTableColumnTitleType>>) =>
    li[0].map((arr, i: number) => li.map(row => get(row, i, null)))
  const headerContent: Array<Array<TrustTableColumnTitleType>> = list.map(li =>
    filter(li.title, "isHeader")
  )
  const bodyContent: Array<Array<TrustTableColumnTitleType>> = list.map(li =>
    reject(li.title, "isHeader")
  )
  return [flattenZipArray(headerContent), flattenZipArray(bodyContent)]
}

const CONVERT_MARGIN_BALANCE_DATA: { [t: string]: Array<string> } = {
  title: ["", ""],
  new: ["KashikabuShinki", "YuushiShinki"],
  return: ["KashikabuHensai", "YuushiHensai"],
  balance: ["KashikabuZan", "YuushiZan"],
  subtraction: ["SashihikiZandaka", "SashihikiZenjituhi"],
  lendingOrLoanSubtraction: ["SashihikiZan", "SashihikiZanZenhi"],
  days: ["KaitenNissuu", ""],
  rate: ["TaishakuBairitsu", ""],
  shortage: ["stockShort"],
  buy: ["buy"],
  sell: ["sell"],
  price: ["TaishakuNedan"],
  negative: ["GyakuHibu"],
}

export const MarginBalanceTable: React.FC<MarginBalanceTableType> = props => {
  const fetchedTableInfo = fetchTableInfo(props.tableColumns)
  const data = props.data || {}
  return (
    <table className={ClassNames("table", props.className)}>
      {fetchedTableInfo.map((tableInfo, i) => {
        const TableType = !i ? "thead" : "tbody"
        return (
          <TableType key={i}>
            {tableInfo.map((tableRow, j) => (
              <tr key={j}>
                {tableRow.map((info, k) => {
                  const TableTag = !i ? "th" : "td"
                  return (
                    <React.Fragment key={k}>
                      <TableTag
                        className={ClassNames(
                          get(info, "cls", ""),
                          props.tableColumns[k].titleCls
                        )}
                      >
                        {get(info, "name", "")}
                      </TableTag>
                      {props.tableColumns[k].list.map((contentsList, l) => {
                        const key = get(info, "id")
                        const contents: {
                          content: Function
                          cls?: string
                        } = get(contentsList.row, key, {
                          content: () => "",
                        })
                        return (
                          <TableTag
                            key={l}
                            className={ClassNames(
                              get(contents, "cls", ""),
                              get(contentsList, "columnCls", "")
                            )}
                          >
                            {contents.content(
                              key
                                ? get(
                                    data,
                                    CONVERT_MARGIN_BALANCE_DATA[key][l],
                                    null
                                  )
                                : null
                            )}
                            {/* {contents.content()} */}
                          </TableTag>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </tr>
            ))}
          </TableType>
        )
      })}
    </table>
  )
}

// テスト的なやつ
export const FixedTable = () => {
  return (
    <table className="table table-fixed table-striped">
      <thead>
        <tr>
          <th className="fix">銘柄名</th>
          <th>コード</th>
          <th>現在値</th>
          <th>前日比</th>
          <th>comment</th>
          <th>更新時間</th>
        </tr>
      </thead>
      <tbody>
        {map(range(50), () => (
          <tr>
            <td className="fix">日立</td>
            <td>6501</td>
            <td>3,600.0</td>
            <td>100.0</td>
            <td>コメント</td>
            <td>15:31</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export const SPBaseTable: React.FC<tableProps> = props => {
  const columnList: Array<ColumnListType> = recreateColumnList(props.list)
  const changeColumnList: Array<ColumnListType> = filterColumnList(columnList)
  const firstColumn = changeColumnList[0].id
  const lastColumn = changeColumnList[changeColumnList.length - 1].id
  const [currentTableData, changeCurrent] = useState<number>(firstColumn)
  const changeCurrentDisplay = (direction: boolean) => {
    const index = findIndex(changeColumnList, { id: currentTableData })
    changeCurrent(changeColumnList[index + (direction ? 1 : -1)].id)
  }
  return (
    <table className={ClassNames("table table-sp", props.className)}>
      <thead>
        <tr>
          {map(columnList, (col: ColumnListType, key: number) => {
            return (
              <th
                key={key}
                className={ClassNames(
                  typeof col.className === "function"
                    ? col.className(col, key)
                    : col.className,
                  { active: col.displaySp || key === currentTableData }
                )}
              >
                {key !== currentTableData ? (
                  typeof col.title === "string" ? (
                    col.title
                  ) : (
                    col.title()
                  )
                ) : (
                  <>
                    <i
                      className={ClassNames("material-icons prev", {
                        disabled: currentTableData === firstColumn,
                      })}
                      onClick={() =>
                        currentTableData !== firstColumn &&
                        changeCurrentDisplay(false)
                      }
                    >
                      chevron_left
                    </i>
                    {typeof col.title === "string" ? col.title : col.title()}
                    <i
                      className={ClassNames("material-icons next", {
                        disabled: currentTableData === lastColumn,
                      })}
                      onClick={() =>
                        currentTableData !== lastColumn &&
                        changeCurrentDisplay(true)
                      }
                    >
                      chevron_right
                    </i>
                  </>
                )}
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {map(props.data, (data: APIResponse.Res, key: number) => {
          return (
            <tr key={key}>
              {columnList.map((col: ColumnListType, j: number) => {
                return (
                  <td
                    key={j}
                    className={ClassNames(
                      typeof col.className === "function"
                        ? col.className(data, key)
                        : col.className,
                      { active: col.displaySp || j === currentTableData }
                    )}
                  >
                    {!col.format || typeof col.format === "string"
                      ? col.format
                      : col.format(data, key)}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export type SpRowTableProps = {
  list: Array<{
    title: string | Function
    value: Function
  }>
  data: object
  pushData?: object
  className?: string
  wrapperCls?: string
  onClick?: Function
}

export const SpRowTable: React.FC<SpRowTableProps> = props => {
  return (
    <div className={props.wrapperCls}>
      <table className={ClassNames("table", props.className)}>
        <tbody>
          {map(props.list, (row: List, i) => (
            <tr
              key={i}
              onClick={e => props.onClick && props.onClick(props.data)}
            >
              <th>
                {isFunction(row.title)
                  ? row.title(props.data, props.pushData)
                  : row.title}
              </th>
              <td>{row.value(props.data, props.pushData)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
